// Ecommerce → Resumen → Dashboard (/ecommerce/dashboard). Ventas, pedidos por
// estado, top productos y carritos abandonados de la tienda.
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, Package, ShoppingBasket, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ESTADO_PEDIDO_BADGE, ecomRpc, usd } from './shared';

interface DashT {
  hoy: { pedidos: number; ventas: number };
  d7: { pedidos: number; ventas: number };
  estados: Record<string, number>;
  top: { titulo: string; unidades: number; ventas: number }[];
  carritos_abandonados: number;
}

export function DashboardWeb() {
  const { data } = useQuery<DashT>({
    queryKey: ['ecom-dashboard'],
    queryFn: () => ecomRpc<DashT>('ecom_dashboard', {}),
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 30000,
  });

  const Kpi = ({ label, valor, sub }: { label: string; valor: string; sub?: string }) => (
    <Card><CardContent className="pt-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold">{valor}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </CardContent></Card>
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><LayoutDashboard className="w-5 h-5" /> Resumen de la tienda</h1>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Kpi label="Ventas de hoy" valor={usd(data?.hoy.ventas)} sub={`${data?.hoy.pedidos ?? 0} pedido(s)`} />
        <Kpi label="Ventas últimos 7 días" valor={usd(data?.d7.ventas)} sub={`${data?.d7.pedidos ?? 0} pedido(s)`} />
        <Kpi label="Pendientes de pago" valor={String(data?.estados?.pendiente_pago ?? 0)} sub="requieren seguimiento" />
        <Kpi label="Carritos abandonados" valor={String(data?.carritos_abandonados ?? 0)} sub="últimos 7 días" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Pedidos por estado (30 días)</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {Object.entries(data?.estados ?? {}).length === 0 && <p className="text-sm text-muted-foreground">Sin pedidos aún.</p>}
            {Object.entries(data?.estados ?? {}).map(([estado, n]) => (
              <div key={estado} className="flex items-center gap-2 text-sm">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ESTADO_PEDIDO_BADGE[estado] ?? ''}`}>
                  {estado.replace('_', ' ')}
                </span>
                <span className="font-semibold ml-auto">{n}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" /> Top productos (30 días)</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                {(data?.top ?? []).map((t, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1.5 text-muted-foreground w-6">{i + 1}</td>
                    <td className="py-1.5">{t.titulo}</td>
                    <td className="py-1.5 text-right text-muted-foreground">{Number(t.unidades)} u.</td>
                    <td className="py-1.5 text-right font-mono">{usd(t.ventas)}</td>
                  </tr>
                ))}
                {!data?.top?.length && <tr><td className="py-4 text-center text-muted-foreground" colSpan={4}>Sin ventas aún.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {Number(data?.carritos_abandonados) > 0 && (
        <div className="flex items-center gap-2 text-sm border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-md p-3">
          <ShoppingBasket className="w-4 h-4 text-amber-600 shrink-0" />
          <span>Hay {data?.carritos_abandonados} carrito(s) abandonado(s) con contacto — recupéralos por WhatsApp desde <b>Carritos Abandonados</b>.</span>
        </div>
      )}
    </div>
  );
}
